import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      // Small delay for better UX (not appearing instantly)
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };

    localStorage.setItem("cookie-consent", JSON.stringify(allPreferences));
    setShowBanner(false);
    setShowPreferences(false);

    // Here you would initialize analytics, marketing scripts, etc.
    console.log("Accepted all cookies:", allPreferences);
  };

  const handleRejectAll = () => {
    const minimalPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };

    localStorage.setItem("cookie-consent", JSON.stringify(minimalPreferences));
    setShowBanner(false);
    setShowPreferences(false);

    console.log("Rejected optional cookies:", minimalPreferences);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookie-consent", JSON.stringify(preferences));
    setShowBanner(false);
    setShowPreferences(false);

    // Initialize only the selected cookie types
    console.log("Saved preferences:", preferences);
  };

  const handleManageCookies = () => {
    setShowPreferences(true);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop overlay for preferences modal */}
      {showPreferences && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity"
          onClick={() => setShowPreferences(false)}
          aria-hidden="true"
        />
      )}

      {/* Cookie Banner */}
      <div
        className={`fixed ${
          showPreferences ? "bottom-0 left-0 right-0" : "bottom-0 left-0 right-0"
        } z-[70] transition-all duration-300`}
        role="dialog"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
        aria-modal={showPreferences ? "true" : undefined}
      >
        {/* Main Banner - Always visible */}
        {!showPreferences && (
          <div className="bg-white border-t-2 border-gray-200 shadow-2xl">
            <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 sm:py-5">
              <div className="flex flex-col sm:flex-row items-start gap-4">
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3
                    id="cookie-banner-title"
                    className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5"
                  >
                    🍪 We Value Your Privacy
                  </h3>
                  <p
                    id="cookie-banner-description"
                    className="text-sm sm:text-base text-gray-600 leading-relaxed"
                  >
                    We use cookies to improve your experience, analyze usage, and personalize content. You can customize your preferences or accept all cookies.{" "}
                    <a
                      href="/cookie-policy"
                      className="text-blue-600 hover:text-blue-700 font-medium underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={handleManageCookies}
                    className="px-5 py-2.5 bg-white border-2 border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] sm:min-w-[140px]"
                    aria-label="Manage cookie preferences"
                  >
                    Manage Cookies
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-5 py-2.5 bg-gray-100 border-2 border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 hover:border-gray-400 active:bg-gray-300 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] sm:min-w-[140px]"
                    aria-label="Reject optional cookies"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] sm:min-w-[140px]"
                    aria-label="Accept all cookies"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preferences Modal - Slides up from banner */}
        {showPreferences && (
          <div className="bg-white border-t-2 border-gray-200 shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="max-w-4xl mx-auto px-4 py-5 sm:px-6 lg:px-8 sm:py-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    Cookie Preferences
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose which cookies you want to allow. You can change these settings anytime.
                  </p>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-1"
                  aria-label="Close preferences"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Cookie Categories */}
              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-gray-900 mb-1">
                        Necessary Cookies
                      </h4>
                      <p className="text-sm text-gray-600">
                        Essential for the website to function properly. These cannot be disabled.
                      </p>
                    </div>
                    <div className="ml-4">
                      <div
                        className="w-12 h-7 bg-blue-600 rounded-full flex items-center cursor-not-allowed opacity-50"
                        role="switch"
                        aria-checked="true"
                        aria-readonly="true"
                        aria-label="Necessary cookies (always enabled)"
                      >
                        <span className="w-5 h-5 bg-white rounded-full shadow-md ml-6 transition-transform"></span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-center">Always On</p>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-gray-900 mb-1">
                        Analytics Cookies
                      </h4>
                      <p className="text-sm text-gray-600">
                        Help us understand how visitors interact with our website to improve user experience.
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        setPreferences({ ...preferences, analytics: !preferences.analytics })
                      }
                      className={`ml-4 w-12 h-7 rounded-full flex items-center transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        preferences.analytics ? "bg-blue-600" : "bg-gray-300"
                      }`}
                      role="switch"
                      aria-checked={preferences.analytics}
                      aria-label="Toggle analytics cookies"
                    >
                      <span
                        className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                          preferences.analytics ? "translate-x-6" : "translate-x-1"
                        }`}
                      ></span>
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-gray-900 mb-1">
                        Marketing Cookies
                      </h4>
                      <p className="text-sm text-gray-600">
                        Used to track visitors across websites to display relevant advertisements.
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        setPreferences({ ...preferences, marketing: !preferences.marketing })
                      }
                      className={`ml-4 w-12 h-7 rounded-full flex items-center transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        preferences.marketing ? "bg-blue-600" : "bg-gray-300"
                      }`}
                      role="switch"
                      aria-checked={preferences.marketing}
                      aria-label="Toggle marketing cookies"
                    >
                      <span
                        className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                          preferences.marketing ? "translate-x-6" : "translate-x-1"
                        }`}
                      ></span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-5 py-3 bg-gray-100 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-200 hover:border-gray-400 active:bg-gray-300 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px]"
                >
                  Reject All
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-5 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px]"
                >
                  Save Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-5 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 active:bg-green-800 transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 min-h-[44px]"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CookieConsent;
